import { z } from 'zod/v4'

/** App event type pattern: app.{namespace}.{event} */
export const AppEventTypeSchema = z
  .string()
  .regex(/^app\.[a-z][a-z0-9_]*(\.[a-z][a-z0-9_]*)+$/, {
    message: 'App event type must match pattern: app.{namespace}.{event}',
  })

export type AppEventType = z.infer<typeof AppEventTypeSchema>

export const AppEventResourceSchema = z.object({
  type: z.string().min(1),
  id: z.string().min(1),
})

export type AppEventResource = z.infer<typeof AppEventResourceSchema>

export const CreateAppEventInputSchema = z.object({
  workplaceId: z.string(),
  name: z.string().min(1),
  payload: z.record(z.string(), z.unknown()),
  app: z.string().optional(),
  context: z.record(z.string(), z.unknown()).optional(),
  resource: AppEventResourceSchema.optional(),
  emittedBy: z.string().optional(),
  appInstallationId: z.string().optional(),
})

export type CreateAppEventInput = z.infer<typeof CreateAppEventInputSchema>

export const CreateAppEventResultSchema = z.object({
  emitted: z.boolean(),
  eventId: z.string().nullable(),
  eventType: z.string(),
})

export type CreateAppEventResult = z.infer<typeof CreateAppEventResultSchema>

/**
 * Provenance of a CRM write, used to break bidirectional sync loops.
 * `appHandle` is present only when the write went through an app installation.
 */
export const EventOriginSchema = z.object({
  /** Activity source: ui | workflow | batch_job | api | agent | … */
  source: z.string().min(1),
  /** Installing app handle when the write was scoped to an app installation */
  appHandle: z.string().min(1).optional(),
  appInstallationId: z.string().min(1).optional(),
})

export type EventOrigin = z.infer<typeof EventOriginSchema>

/**
 * Build a normalized app event type from namespace and event name.
 * @example buildAppEventType('bft', 'member.updated') => 'app.bft.member.updated'
 */
export function buildAppEventType(app: string, name: string): string {
  const normalizedApp = app.trim().toLowerCase()
  const normalizedName = name.trim().toLowerCase()
  const eventType = `app.${normalizedApp}.${normalizedName}`
  AppEventTypeSchema.parse(eventType)
  return eventType
}

// -----------------------------------------------------------------------------
// Trigger Match Config — structured conditions for sentence-style triggers
// -----------------------------------------------------------------------------

/** Field condition: compare a payload path against a value or transition */
export const TriggerFieldConditionSchema = z.object({
  kind: z.literal('field'),
  /** Payload path, e.g. "data.after.status", "data.participant.kind" */
  path: z.string().min(1),
  /** Human-readable label for the field */
  fieldLabel: z.string(),
  /** Comparison operator */
  op: z.enum([
    'eq',
    'neq',
    'gt',
    'gte',
    'lt',
    'lte',
    'contains',
    'changed_from_to',
  ]),
  /** Scalar value for standard ops */
  value: z.unknown().optional(),
  /** For changed_from_to: previous value (null = Any) */
  from: z.unknown().nullable().optional(),
  /** For changed_from_to: new value (null = Any) */
  to: z.unknown().nullable().optional(),
})

export type TriggerFieldCondition = z.infer<typeof TriggerFieldConditionSchema>

/** Relative date condition: compare a date field against a relative offset from now */
export const TriggerRelativeDateConditionSchema = z.object({
  kind: z.literal('relative_date'),
  /** Payload path to date field, e.g. "data.record.created_at" */
  path: z.string().min(1),
  /** Human-readable label for the field */
  fieldLabel: z.string(),
  /**
   * Comparison operator.
   * - Legacy: lt/gt/lte/gte (“less/more than X ago”)
   * - Preferred: before/after relative to a past or future offset
   */
  op: z.enum(['lt', 'gt', 'lte', 'gte', 'before', 'after']),
  /** Number of units */
  amount: z.number().int().positive(),
  /** Time unit */
  unit: z.enum(['minutes', 'hours', 'days', 'weeks']),
  /**
   * Whether the offset is in the past (“1 week ago”) or future (“1 week from now”).
   * Defaults to past for backward compatibility.
   */
  direction: z.enum(['past', 'future']).optional(),
})

export type TriggerRelativeDateCondition = z.infer<
  typeof TriggerRelativeDateConditionSchema
>

/** Union of condition types */
export const TriggerConditionSchema = z.discriminatedUnion('kind', [
  TriggerFieldConditionSchema,
  TriggerRelativeDateConditionSchema,
])

export type TriggerCondition = z.infer<typeof TriggerConditionSchema>

/** CRM operations */
export const CrmOperationSchema = z.enum(['created', 'updated', 'deleted'])
export type CrmOperation = z.infer<typeof CrmOperationSchema>

/** Event sources */
export const TriggerSourceSchema = z.enum(['thread', 'crm', 'app'])
export type TriggerSource = z.infer<typeof TriggerSourceSchema>

/**
 * Structured match configuration for event subscriptions.
 * Replaces free-form Liquid conditions with a structured AND-joined filter set.
 */
export const TriggerMatchConfigSchema = z.object({
  /** Schema version for future migrations */
  version: z.literal(1),
  /** Event source category */
  source: TriggerSourceSchema,
  /** Resolved event type (stored for display + matching) */
  eventType: z.string().min(1),
  /** Human-readable label seed, e.g. "Customer", "Member" */
  label: z.string(),
  // CRM-specific
  /** CRM model handle (for CRM source) */
  model: z.string().optional(),
  /** CRM operation (for CRM source) */
  operation: CrmOperationSchema.optional(),
  // App-specific
  /** App handle (for app source) */
  appHandle: z.string().optional(),
  /** App event name (for app source) */
  appEventName: z.string().optional(),
  /** AND-joined conditions (empty = match all events of this type) */
  conditions: z.array(TriggerConditionSchema).default([]),
})

export type TriggerMatchConfig = z.infer<typeof TriggerMatchConfigSchema>

/**
 * Parse and validate matchConfig from JSON.
 * Returns null if input is null/undefined or fails validation.
 */
export function parseTriggerMatchConfig(
  raw: unknown,
): TriggerMatchConfig | null {
  if (raw == null) return null
  const result = TriggerMatchConfigSchema.safeParse(raw)
  return result.success ? result.data : null
}

/**
 * Thread-scoped subscriptions only match when the workflow/agent is on
 * the thread. CRM and app sources are workplace-wide.
 */
export function isThreadEventSubscription(
  eventType: string,
  matchConfig?: unknown,
): boolean {
  const parsed = parseTriggerMatchConfig(matchConfig)
  if (parsed?.source === 'thread') return true
  if (parsed?.source === 'crm' || parsed?.source === 'app') return false
  return eventType.startsWith('thread.')
}

/** Thread-source subscriptions only — CRM/app recipes fire workplace-wide. */
export function filterThreadEventSubscriptions<
  T extends { eventType: string; matchConfig?: unknown },
>(subscriptions: T[]): T[] {
  return subscriptions.filter((subscription) =>
    isThreadEventSubscription(subscription.eventType, subscription.matchConfig),
  )
}
